"use client";

import React, { useState, useEffect, Children, isValidElement, useMemo } from "react";
import { createPortal } from "react-dom";
import { usePathname } from "next/navigation";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff } from "lucide-react";
import { outlineToggleClass } from "@/lib/quiz-control-styles";
import { useArticleShellSlots } from "@/components/article-shell/ArticleShellContext";
import { useEffectivePass } from "@/hooks/useEffectivePass";
import { ExamScoreProvider, useExamScore } from "@/components/ExamScoreContext";
import ExamScoreModal from "@/components/ExamScoreModal";
import ArticleScorePanel from "@/components/ArticleScorePanel";
import ProblemListNav from "@/components/ProblemListNav";
import { ProblemHistoryProvider } from "@/components/ProblemHistoryContext";
import { ProblemDataProvider } from "@/components/ProblemDataContext";
import { ChoicesVisibilityProvider, useChoicesVisibility } from "@/components/ChoicesVisibilityContext";
import { getProblemHistoriesBatch, getProblemHistoriesBatchBySessionId } from "@/lib/server-actions/problem/read";
import { getAnonymousSessionId } from "@/lib/anonymousSession";
import { useUser } from "@clerk/nextjs";
import { ProblemHistory } from "@/lib/types";
import AdSenseInlineProblem from "@/components/AdSenseInlineProblem";
import { handleServerActionError } from "@/lib/serverActionUtils";
import { useProblemsQuery } from "@/hooks/useProblemQuery";
import {
  getProblemAnswerSessionState,
  hasRuntimeProblemAnswerSessionState,
} from "@/lib/random-problems-persistence";
import type { ProblemScoreStatus } from "@/lib/problem-score-status";

// 같은 JS 세션 안에서 LayoutSwitch 등으로 RandomizedProblems만 리마운트될 때,
// 이미 사용자가 보고 있던 셔플 순서를 복원한다. F5/페이지 리로드 시에는 모듈이
// 다시 평가되어 비워지므로 "새로고침 시 새 문제/새 순서" 동작과 충돌하지 않는다.
const shuffledOrderSessionCache = new Map<string, number[]>();

const isUsableShuffledOrder = (
  order: number[] | undefined,
  count: number,
): order is number[] => {
  if (!order || order.length !== count) return false;
  const seen = new Set<number>();
  for (const index of order) {
    if (!Number.isInteger(index) || index < 0 || index >= count || seen.has(index)) {
      return false;
    }
    seen.add(index);
  }
  return true;
};

// 전체 리로드는 위 세션 캐시를 비우므로, 백그라운드 복귀 복원(JCG-2895)이 같은 세트를 되살려도
// 순서만 다시 섞였다(JCG-3510). 그래서 셔플한 표시 순서를 localStorage 에도 남겨 두고, 부모가 허용할 때만
// 되살린다. 인덱스가 아니라 문제 id 목록으로 저장해, 입력 순서가 달라도 같은 문제 집합이면 복원한다.
const SHUFFLED_ORDER_STORAGE_PREFIX = "randomized-problems-order-";

const getShuffledOrderStorageKey = (storageKey: string): string =>
  `${SHUFFLED_ORDER_STORAGE_PREFIX}${storageKey}`;

const getChildProblemId = (child: React.ReactNode): string | null => {
  if (!React.isValidElement(child)) return null;
  const id = (child.props as { id?: string }).id;
  return typeof id === "string" ? id : null;
};

const saveShuffledOrder = (
  storageKey: string,
  order: number[],
  childIds: (string | null)[],
): void => {
  const orderedIds = order.map((index) => childIds[index]);
  // id 없는 child 가 있으면 순서를 id 로 표현할 수 없으므로 저장하지 않는다.
  if (orderedIds.some((id) => id === null)) return;
  try {
    localStorage.setItem(getShuffledOrderStorageKey(storageKey), JSON.stringify(orderedIds));
  } catch {
    // localStorage 접근 불가 시 무시
  }
};

// 저장된 표시 순서를 현재 children 인덱스 순서로 바꾼다. 문제 집합이 다르거나 손상됐으면 null.
// id 누락·중복으로 1:1 대응이 안 되면 완전한 순열이 나올 수 없어 isUsableShuffledOrder 가 거른다.
const getStoredShuffledOrder = (
  storageKey: string,
  childIds: (string | null)[],
): number[] | null => {
  try {
    const stored = localStorage.getItem(getShuffledOrderStorageKey(storageKey));
    if (!stored) return null;
    const parsed: unknown = JSON.parse(stored);
    if (!Array.isArray(parsed)) return null;

    const indexById = new Map<string, number>();
    childIds.forEach((id, index) => {
      if (id !== null) indexById.set(id, index);
    });

    const order = parsed.map((id) =>
      typeof id === "string" ? indexById.get(id) ?? -1 : -1,
    );
    return isUsableShuffledOrder(order, childIds.length) ? order : null;
  } catch {
    return null;
  }
};

const ChoicesToggleBar: React.FC<{ headerSlot?: React.ReactNode }> = ({ headerSlot }) => {
  const ctx = useChoicesVisibility();
  const { isPremiumOrAbove } = useEffectivePass();
  // 셸(데스크탑 글 페이지)이면 컨트롤을 우측 사이드바 필기 위 슬롯으로 portal (JCG-2066).
  const { inShell, quizControlsSlot } = useArticleShellSlots();
  const inSidebar = inShell && !!quizControlsSlot;

  // 전체 보기 숨기기 토글 (프리미엄 이상 전용)
  const toggleButton =
    ctx && isPremiumOrAbove ? (
      <Button
        onClick={ctx.toggleGlobalChoicesHidden}
        variant="outline"
        size="sm"
        className={outlineToggleClass(ctx.globalChoicesHidden)}
      >
        {ctx.globalChoicesHidden ? (
          <>
            <Eye className="h-4 w-4 mr-1" />
            전체 보기 보이기
          </>
        ) : (
          <>
            <EyeOff className="h-4 w-4 mr-1" />
            전체 보기 숨기기
          </>
        )}
      </Button>
    ) : null;

  // 표시할 컨트롤이 없으면 아무것도 렌더하지 않음
  if (!headerSlot && !toggleButton) return null;

  // 셸: 우측 사이드바 필기 위 슬롯에 세로 스택 카드로 portal.
  // 좁은 사이드바라 두 토글 버튼을 모두 full-width 로(카드 레벨에서 일괄 강제).
  if (inSidebar) {
    return createPortal(
      <div className="flex flex-col gap-2 rounded-xl border border-border bg-card p-3 [&_button]:w-full">
        {headerSlot}
        {toggleButton}
      </div>,
      quizControlsSlot,
    );
  }

  // 비셸(모바일/클래식): 기존 본문 상단 인라인 바
  return (
    <div className="mb-4 flex items-center justify-between">
      {headerSlot ?? <div />}
      {toggleButton ?? <div />}
    </div>
  );
};

interface RandomizedProblemsProps {
  children: React.ReactNode;
  headerSlot?: React.ReactNode;
  shuffle?: boolean;
  onProblemOrderChange?: (problemIds: string[]) => void;
  restoreProblemResultsAcrossReload?: boolean;
  /** 셔플한 표시 순서를 리로드 너머로 저장할 네임스페이스. 없으면 저장·복원하지 않는다(JCG-3510). */
  orderStorageKey?: string;
  /** true 면 세션 캐시가 빈 전체 리로드에서 orderStorageKey 에 저장된 표시 순서를 복원한다(JCG-3510). */
  restoreOrderAcrossReload?: boolean;
  scoreTitle?: string;
  /**
   * 표시 중인 문제를 모두 채점하면 호출된다(JCG-3865 감자시험 도전 횟수). 복원된 채점 상태로 이미 완료된
   * 세트나 재채점에서도 다시 호출될 수 있으므로, 부모가 세트 단위로 중복을 거른다.
   */
  onAllProblemsScored?: (problemIds: string[]) => void;
  /**
   * 부모(RandomProblems)가 보기 설정 컨트롤을 직접 소유할 때 true — 내부 ChoicesToggleBar 를
   * 렌더하지 않고, ChoicesVisibilityProvider 도 부모 것을 재사용한다(중첩 방지, JCG-2066).
   */
  suppressControls?: boolean;
}

/** 표시 중인 문제가 모두 채점 상태(세션 채점 + 복원)가 되면 부모에 알린다 (JCG-3865). */
const AllProblemsScoredWatcher: React.FC<{
  problemIds: string[];
  onAllScored: (problemIds: string[]) => void;
}> = ({ problemIds, onAllScored }) => {
  const examScore = useExamScore();
  const resultsById = examScore?.problemResultsById;

  useEffect(() => {
    if (!resultsById || problemIds.length === 0) return;
    if (problemIds.every((id) => resultsById[id] !== undefined)) {
      onAllScored(problemIds);
    }
  }, [problemIds, resultsById, onAllScored]);

  return null;
};

interface ProblemNavItem {
  id?: string;
  preview?: string;
}

const RandomizedProblems: React.FC<RandomizedProblemsProps> = ({
  children,
  headerSlot,
  shuffle = true,
  onProblemOrderChange,
  restoreProblemResultsAcrossReload = false,
  orderStorageKey,
  restoreOrderAcrossReload = false,
  scoreTitle,
  onAllProblemsScored,
  suppressControls,
}) => {
  const pathname = usePathname();
  // 부모(RandomProblems)가 ChoicesVisibilityProvider 를 제공하면 그걸 재사용(중첩 시 상태 분리됨).
  const parentChoices = useChoicesVisibility();
  // 셔플 결과를 element 가 아니라 "인덱스 순서(number[])"로 저장한다(JCG-2269).
  // element 를 저장하면 부모 리렌더로 children 식별성이 바뀔 때마다 재셔플돼 채점마다 리셋되므로,
  // 순서만 보관하고 렌더 시 최신 validChildren 에 매핑한다(데이터/번호 cloneElement 는 최신 children 사용).
  const [shuffledOrder, setShuffledOrder] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [problemHistories, setProblemHistories] = useState<Record<string, ProblemHistory | null>>({});
  const [initialProblemResultsById, setInitialProblemResultsById] = useState<
    Record<string, ProblemScoreStatus>
  >({});
  // 복원된 문제별 정확 점수 비율(0~1). 부분점수를 status 근사(0.5) 대신 정확히 되살린다(JCG-2806).
  const [initialProblemScoreById, setInitialProblemScoreById] = useState<
    Record<string, number>
  >({});
  const [isLoadingHistories, setIsLoadingHistories] = useState(true);
  const { isSignedIn, isLoaded: isUserLoaded, user } = useUser();

  // 유효한 children만 추출 (셔플·스켈레톤 개수 계산에서 공용)
  const validChildren = useMemo(
    () => Children.toArray(children).filter(isValidElement),
    [children],
  );

  // children에서 ProblemAnswerFetcher의 id 추출
  const problemIds = useMemo(() => {
    const ids: string[] = [];

    const traverse = (node: React.ReactNode) => {
      if (React.isValidElement(node)) {
        const props = node.props as { id?: string; children?: React.ReactNode };
        if (props?.id && typeof props.id === 'string') {
          ids.push(props.id);
        }
        if (props?.children) {
          Children.forEach(props.children, traverse);
        }
      } else if (Array.isArray(node)) {
        node.forEach(traverse);
      }
    };

    Children.forEach(children, traverse);
    return ids;
  }, [children]);

  // 셔플 effect 를 children 참조가 아니라 "문제 ID 집합"에 묶기 위한 안정 키(JCG-2269).
  // 부모 리렌더(useUser 갱신·채점 등)로 children 배열만 새로 만들어져도 ID 집합이 같으면 재셔플하지 않는다.
  // validChildren.length 를 접두로 포함해, id 없는 child 등으로 길이만 바뀌어도 idsKey 가 갱신되게 한다
  // (그렇지 않으면 shuffledOrder 길이가 어긋나 shuffledChildren 이 [] 가 되어 빈 화면이 될 수 있음).
  const idsKey = useMemo(
    () => `${validChildren.length}:${problemIds.join(",")}`,
    [validChildren.length, problemIds]
  );

  // React Query로 문제 데이터 조회 (IndexedDB 캐싱 포함)
  const { data: problemData, isLoading: isLoadingProblems } = useProblemsQuery(problemIds);

  // 문제 이력 배치 조회 — 외부 데이터 fetch, JCG-2034 마이그레이션 예정
  useEffect(() => {
    if (!isUserLoaded) {
      return;
    }

    if (problemIds.length === 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsLoadingHistories(false);
      return;
    }

    const loadHistories = async () => {
      setIsLoadingHistories(true);
      try {
        if (isSignedIn && user) {
          const result = await getProblemHistoriesBatch(problemIds);
          if (result.success) {
            setProblemHistories(result.histories);
          }
        } else {
          const sessionId = getAnonymousSessionId();
          if (sessionId) {
            const result = await getProblemHistoriesBatchBySessionId(sessionId, problemIds);
            if (result.success) {
              setProblemHistories(result.histories);
            }
          }
        }
      } catch (err) {
        if (handleServerActionError(err)) {
          return;
        }
        console.error("문제 이력 배치 조회 실패:", err);
      } finally {
        setIsLoadingHistories(false);
      }
    };

    loadHistories();
  }, [problemIds, isUserLoaded, isSignedIn, user]);

  // 셔플 효과 — Math.random 기반 비순수 계산, useMemo는 purity 룰 위반.
  // deps 는 idsKey(문제 ID 집합)에 묶는다(JCG-2269) — 부모 리렌더로 children 식별성만 바뀌고 ID 집합이
  // 같으면 재셔플하지 않아, 채점마다 스켈레톤 깜빡임 + 문제 리셋이 발생하지 않는다.
  // 셔플 결과는 element 가 아니라 인덱스 순서(number[])로 저장한다 — 렌더 시 최신 validChildren 에 매핑.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsLoading(true);

    const count = validChildren.length;
    const order = Array.from({ length: count }, (_, i) => i);

    if (!shuffle) {
      setShuffledOrder(order);
      setIsLoading(false);
      return;
    }

    const cachedOrder = shuffledOrderSessionCache.get(idsKey);
    const hasCachedOrder = isUsableShuffledOrder(cachedOrder, count);
    // 세션 캐시로 끝나는 리사이즈 리마운트에서는 child id 목록이 필요 없다.
    const childIds = hasCachedOrder ? [] : validChildren.map(getChildProblemId);
    // 세션 캐시가 없으면, 백그라운드 복귀 리로드처럼 부모가 같은 세트를 복원한 경우에만 저장된 표시 순서를
    // 되살린다(JCG-3510). 능동 새로고침·다시 풀기·SPA 재진입은 restoreOrderAcrossReload=false 라 새로 섞는다.
    const restoredOrder = hasCachedOrder
      ? cachedOrder
      : restoreOrderAcrossReload && orderStorageKey
        ? getStoredShuffledOrder(orderStorageKey, childIds)
        : null;
    if (restoredOrder) {
      shuffledOrderSessionCache.set(idsKey, restoredOrder);
      setShuffledOrder(restoredOrder);
      setIsLoading(false);
      return;
    }

    const timer = setTimeout(() => {
      // Fisher-Yates shuffle algorithm — 인덱스 순서만 섞는다.
      for (let i = order.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [order[i], order[j]] = [order[j], order[i]];
      }

      shuffledOrderSessionCache.set(idsKey, order);
      if (orderStorageKey) saveShuffledOrder(orderStorageKey, order, childIds);
      setShuffledOrder(order);
      setIsLoading(false);
    }, 500);

    // idsKey 가 500ms 안에 또 바뀌면 stale 타이머가 길이 안 맞는 order 를 세팅해 빈 화면이 될 수 있어 정리한다.
    return () => clearTimeout(timer);
    // idsKey 에 validChildren.length 를 포함하므로 길이/ID 집합 변화가 모두 반영된다.
    // orderStorageKey·restoreOrderAcrossReload 는 이 ID 집합의 순서를 정할 때의 값만 쓴다 — 복원 플래그가
    // 나중에 내려가도 이미 정한 순서를 다시 섞지 않는다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, shuffle]);

  // 셔플 순서(인덱스)를 최신 validChildren 에 매핑한 배열(JCG-2269).
  // idsKey 가 같으면 순서가 보존돼 채점/리렌더 시 깜빡임·리셋이 없다. 길이가 어긋난 stale 순서는 무시.
  const shuffledChildren = useMemo(() => {
    if (shuffledOrder.length !== validChildren.length) {
      return [];
    }
    return shuffledOrder.map((sourceIndex) => validChildren[sourceIndex]);
  }, [shuffledOrder, validChildren]);

  const displayedProblemIds = useMemo(
    () =>
      shuffledChildren
        .map(getChildProblemId)
        .filter((id): id is string => id !== null),
    [shuffledChildren],
  );

  useEffect(() => {
    const restoredResults: Record<string, ProblemScoreStatus> = {};
    const restoredScores: Record<string, number> = {};

    for (const problemId of displayedProblemIds) {
      const sessionState = getProblemAnswerSessionState(pathname, problemId);
      const canRestoreStatus =
        restoreProblemResultsAcrossReload ||
        hasRuntimeProblemAnswerSessionState(pathname, problemId);
      if (canRestoreStatus && sessionState?.scoreStatus) {
        restoredResults[problemId] = sessionState.scoreStatus;
        if (typeof sessionState.scoreRatio === "number") {
          restoredScores[problemId] = sessionState.scoreRatio;
        }
      }
    }

    // 계약(JCG-2806): 아래 두 setter 는 반드시 같은 배치(같은 effect·동기)로 함께 set 해야 한다.
    // ExamScoreContext 폴딩이 id별 1회만 집계(dedup ref)하므로, results 가 먼저 도착해 status 근사로
    // 집계·잠긴 뒤 정확 ratio 가 뒤늦게 오면 무시되는 회귀가 생긴다. 분리하지 말 것.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setInitialProblemResultsById(restoredResults);
    setInitialProblemScoreById(restoredScores);
  }, [pathname, displayedProblemIds, restoreProblemResultsAcrossReload]);

  useEffect(() => {
    if (displayedProblemIds.length === 0) return;
    onProblemOrderChange?.(displayedProblemIds);
  }, [displayedProblemIds, onProblemOrderChange]);

  // 문제 번호 주입 + 문제 3개마다 AdSense 인라인 광고 삽입
  const childrenWithAds = useMemo(() => {
    if (isLoading || shuffledChildren.length === 0) {
      return [];
    }

    const result: React.ReactNode[] = [];
    const totalProblems = shuffledChildren.length;

    shuffledChildren.forEach((child, index) => {
      const numberedChild = React.isValidElement(child)
        ? React.cloneElement(child as React.ReactElement<Record<string, unknown>>, {
            problemNumber: index + 1,
            totalProblems,
          })
        : child;
      // 좌측 문제 목록 네비(ProblemListNav)가 스크롤할 앵커. 순번 기반이라 문제 id 유무와 무관.
      // scroll-mt 로 셸 상단 헤더에 가리지 않게 여백 확보.
      result.push(
        <div key={`q-${index}`} id={`quiz-problem-${index + 1}`} className="scroll-mt-24">
          {numberedChild}
        </div>,
      );

      if ((index + 1) % 3 === 0 && index < totalProblems - 1) {
        result.push(<AdSenseInlineProblem key={`ad-${index}`} />);
      }
    });

    return result;
  }, [shuffledChildren, isLoading]);

  // 좌측 문제 목록 네비(ProblemListNav)용 ID + 미리보기 텍스트 — 셔플 순서(= 문제 번호) 정렬.
  // 각 child(ProblemAnswerFetcher)의 id 로 problemData 에서 problem 본문을 찾는다(로딩 전엔 undefined).
  const problemNavItems = useMemo<ProblemNavItem[]>(
    () =>
      shuffledChildren.map((child) => {
        if (!React.isValidElement(child)) return {};
        const id = (child.props as { id?: string }).id;
        if (!id) return {};
        return {
          id,
          preview: problemData?.[id]?.problem,
        };
      }),
    [shuffledChildren, problemData],
  );

  // 로딩(셔플) 중에도 providers·사이드바 컨트롤(보기설정·점수·문제목록)은 유지하고 문제 영역만
  // 스켈레톤으로 교체한다 — 안 푼 문제만 토글/재셔플 시 사이드바가 사라지지 않도록(JCG-2066).
  const childrenCount = validChildren.length;
  const problemArea = isLoading ? (
    <div className="space-y-4">
      {Array.from({ length: childrenCount }).map((_, index) => (
        <div key={index} className="space-y-3">
          <Skeleton className="h-6 w-3/4" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
          <div className="flex gap-2">
            <Skeleton className="h-8 w-16" />
            <Skeleton className="h-8 w-16" />
            <Skeleton className="h-8 w-16" />
            <Skeleton className="h-8 w-16" />
          </div>
        </div>
      ))}
    </div>
  ) : (
    childrenWithAds
  );

  const inner = (
    <ProblemDataProvider problems={problemData ?? {}} isLoading={isLoadingProblems}>
      <ProblemHistoryProvider histories={problemHistories} isLoading={isLoadingHistories}>
        <ExamScoreProvider
          key={idsKey}
          initialProblemResultsById={initialProblemResultsById}
          initialProblemScoreById={initialProblemScoreById}
        >
          {/* 부모가 컨트롤을 소유하면(suppressControls) 내부 바는 렌더하지 않음 */}
          {!suppressControls && <ChoicesToggleBar headerSlot={headerSlot} />}
          {problemArea}
          {/* 셸: 좌측 점수 패널 + 문제 목록 네비 / 모바일 비셸: 하단 점수 도구 (JCG-2066/JCG-2221) */}
          <ArticleScorePanel />
          <ProblemListNav problems={problemNavItems} />
          <ExamScoreModal scoreTitle={scoreTitle} />
          {onAllProblemsScored && !isLoading && (
            <AllProblemsScoredWatcher
              problemIds={displayedProblemIds}
              onAllScored={onAllProblemsScored}
            />
          )}
        </ExamScoreProvider>
      </ProblemHistoryProvider>
    </ProblemDataProvider>
  );

  // 부모 provider 가 있으면 재사용, 없으면(독립 사용 페이지 등) 자체 제공.
  return parentChoices ? inner : <ChoicesVisibilityProvider>{inner}</ChoicesVisibilityProvider>;
};

RandomizedProblems.displayName = 'RandomizedProblems';

export default RandomizedProblems;
